'use client'

import { useEffect, useRef, useState } from 'react'

import { faClose, faBookmark } from '@fortawesome/pro-regular-svg-icons'
import { faBookmark as faBookmarkSolid } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useClickAway } from '@uidotdev/usehooks'
import { useSearchParams } from 'next/navigation'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  FormattedMessage,
  SpinnerDots,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { PermissionGranule, usePermission } from '@/helpers/permissions'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useSavedSearch } from './SavedSearchContext'

const TOAST_AUTO_DISMISS_MS = 6_000

const Icon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
`
const StyledButton = styled(Button).attrs({
  size: 'small',
  type: 'button',
})<{ $isSavedSearchImprovementsEnabled?: boolean }>`
  position: relative;
  font-size: 0.75rem;

  span {
    display: inherit;
    gap: inherit;
    align-items: inherit;
    justify-content: inherit;
  }

  div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
  }

  &:has(div) span {
    opacity: 0;
  }

  &.active:disabled {
    color: ${Colors.Semantic.BStock.default.lightBackground};
    background: ${({ $isSavedSearchImprovementsEnabled }) =>
      $isSavedSearchImprovementsEnabled
        ? Colors.Semantic.Neutral.white
        : 'transparent'};
    ${({ $isSavedSearchImprovementsEnabled }) =>
      $isSavedSearchImprovementsEnabled &&
      `border: 1px solid ${Colors.Semantic.BStock.default.lightBackground};`}
  }

  &:disabled {
    cursor: default;
  }
`
// Why the added specificity? See https://bstocksolutions.slack.com/archives/C04225G5QCS/p1751387142312069
const StyledTooltip = styled(Tooltip)`
  && {
    pointer-events: initial;
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
  }
`

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-start;
  max-width: 19.4rem;
  ${Typography.Body3}
`

const CloseButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;

  color: ${Colors.Semantic.Neutral.white};

  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`

const SaveButton = () => {
  const { trackButtonClicked } = useAnalytics()

  const searchParams = useSearchParams()
  const id = searchParams?.get('id')

  const {
    createSavedSearch,
    hasValidCurrentUserSavedSearch,
    isCreating,
    showTooltip,
    toggleTooltip,
  } = useSavedSearch()
  const {
    state: { filters },
  } = useListingSearch()

  const canReadSavedSearches = usePermission(
    PermissionGranule.FEBP_SAVED_SEARCHES_READ
  )
  const isLoggedIn = useIsLoggedIn()
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hoverRef = useRef<HTMLSpanElement>(null)
  const [isHoveringButton, setIsHoveringButton] = useState(false)

  const isSavedSearchImprovementsEnabled = useFlag(
    FeatureFlags.ENABLE_SAVED_SEARCH_UX_IMPROVEMENTS
  )

  const ref = useClickAway<HTMLSpanElement>((event: Event) => {
    if (tooltipRef.current?.contains(event.target as Node)) return
    toggleTooltip(false)
  })

  useEffect(() => {
    if (!showTooltip) return
    const timeout = setTimeout(
      () => toggleTooltip(false),
      TOAST_AUTO_DISMISS_MS
    )
    return () => clearTimeout(timeout)
  }, [showTooltip, toggleTooltip])

  if (!canReadSavedSearches && isLoggedIn) {
    return null
  }

  enum ButtonName {
    SAVE_SEARCH = 'save_search',
  }

  const trackButtonClickedWrapper = (data: {
    buttonName: ButtonName
    search_term?: string | null
  }) => {
    trackButtonClicked({
      screenName: 'all_auction',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      ...data,
    })
  }

  const handleSave = () => {
    trackButtonClickedWrapper({
      buttonName: ButtonName.SAVE_SEARCH,
      search_term: filters.q,
    })
    return createSavedSearch(filters)
  }

  const buttonLabelId = isSavedSearchImprovementsEnabled
    ? hasValidCurrentUserSavedSearch
      ? 'SavedSearch.savedFilters'
      : 'SavedSearch.saveFilters'
    : hasValidCurrentUserSavedSearch
      ? 'SavedSearch.saved'
      : 'SavedSearch.save'

  return (
    <span ref={ref}>
      <span
        ref={hoverRef}
        onMouseEnter={() => setIsHoveringButton(true)}
        onMouseLeave={() => setIsHoveringButton(false)}
      >
        <StyledButton
          $isSavedSearchImprovementsEnabled={isSavedSearchImprovementsEnabled}
          appearance={'primary'}
          className={hasValidCurrentUserSavedSearch ? 'active' : undefined}
          disabled={hasValidCurrentUserSavedSearch || isCreating || !!id}
          onClick={handleSave}
        >
          {isCreating && <SpinnerDots />}

          <span>
            {!isSavedSearchImprovementsEnabled && (
              <Icon
                icon={
                  hasValidCurrentUserSavedSearch ? faBookmarkSolid : faBookmark
                }
              />
            )}
            <FormattedMessage id={buttonLabelId} />
          </span>
        </StyledButton>
      </span>

      {isSavedSearchImprovementsEnabled && (
        <Tooltip
          isOpen={
            isHoveringButton &&
            !hasValidCurrentUserSavedSearch &&
            !isCreating &&
            !id
          }
          referenceElement={
            // oxlint-disable-next-line react/refs
            hoverRef.current
          }
        >
          <FormattedMessage id="SavedSearch.saveFiltersTooltip" />
        </Tooltip>
      )}

      {/* oxlint-disable-next-line react/refs */}
      <StyledTooltip isOpen={showTooltip} referenceElement={ref.current}>
        <TooltipContent ref={tooltipRef}>
          <p>
            <FormattedMessage id="SavedSearch.searchSaved" />{' '}
            <FormattedMessage id="SavedSearch.willNotify" />
          </p>
        </TooltipContent>

        <CloseButton
          aria-label="Close tooltip"
          onClick={() => toggleTooltip(false)}
        >
          <Icon icon={faClose} />
        </CloseButton>
      </StyledTooltip>
    </span>
  )
}

export default SaveButton
